/**
 * Browser-friendly Signal Protocol implementation for SALT using
 * `libsignal-protocol` (the JS distribution of libsignal-protocol-javascript).
 *
 * Why this: Signal's official `@signalapp/libsignal-client` is Node-native (.node)
 * and cannot be bundled into a CRA/Webpack browser app. The @privacyresearch TS port
 * pulls in emscripten artifacts that reference Node core modules (fs/path) and breaks
 * CRA builds without custom webpack polyfills. `libsignal-protocol` works in browsers.
 */
import nacl from 'tweetnacl';
import * as naclUtil from 'tweetnacl-util';
import { supabase } from '../supabase';
import { debug } from '../../../lib/utils/debug';

// Side-effect import: attaches `libsignal` to the global scope.
import 'libsignal-protocol/dist/libsignal-protocol.js';

declare global {
  interface Window {
    libsignal: any;
  }
}

const DEVICE_ID = 1;
const STORAGE_PREFIX = 'signal_v2';
const SENDER_COPY_KEY_PREFIX = `${STORAGE_PREFIX}_sender_copy_key_`;
const GROUP_SENDER_KEY_PREFIX = `${STORAGE_PREFIX}_group_sender_key_`;

type LibsignalCiphertext = { type: number; bodyB64: string };

export type SignalV2Envelope =
  | {
      v: 2;
      protocol: 'libsignal';
      kind: 'direct';
      from: string;
      to: string;
      recipient: LibsignalCiphertext;
      senderCopy?: { nonceB64: string; ciphertextB64: string };
    }
  | {
      v: 2;
      protocol: 'libsignal';
      kind: 'group';
      groupId: string;
      from: string;
      payload: { nonceB64: string; ciphertextB64: string };
      encryptedSenderKeys: Record<string, LibsignalCiphertext>;
    };

function libsignal(): any {
  const ls = typeof window !== 'undefined' ? (window as any).libsignal : undefined;
  if (!ls) throw new Error('libsignal not available (did the bundle load?)');
  return ls;
}

function uint8ToArrayBuffer(u8: Uint8Array): ArrayBuffer {
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
}

function arrayBufferToUint8(ab: ArrayBuffer): Uint8Array {
  return new Uint8Array(ab);
}

function abToB64(ab: ArrayBuffer): string {
  return naclUtil.encodeBase64(new Uint8Array(ab));
}

function b64ToAb(b64: string): ArrayBuffer {
  const u8 = naclUtil.decodeBase64(b64);
  return uint8ToArrayBuffer(u8);
}

// SessionCipher.body is a "binary string" (may contain NULs). Store as base64.
function binaryStringToBase64(s: string): string {
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i) & 0xff;
  return naclUtil.encodeBase64(bytes);
}

function base64ToBinaryString(b64: string): string {
  const bytes = naclUtil.decodeBase64(b64);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += String.fromCharCode(bytes[i]);
  return out;
}

/**
 * Pack registrationId into signed_prekey_id to avoid requiring a DB migration.
 * registrationId is 14 bits; signedPreKeyIdHigh fits in remaining bits.
 */
function packSignedPreKeyId(signedPreKeyIdHigh: number, registrationId: number): number {
  return (signedPreKeyIdHigh << 14) | (registrationId & 0x3fff);
}

function unpackRegistrationIdFromSignedPreKeyId(packed: number): number {
  return packed & 0x3fff;
}

function ensureSenderCopyKey(userId: string): Uint8Array {
  const keyName = `${SENDER_COPY_KEY_PREFIX}${userId}`;
  const existing = localStorage.getItem(keyName);
  if (existing) return naclUtil.decodeBase64(existing);
  const key = nacl.randomBytes(32);
  localStorage.setItem(keyName, naclUtil.encodeBase64(key));
  return key;
}

function getGroupSenderKeyLocal(groupId: string): Uint8Array | null {
  const keyName = `${GROUP_SENDER_KEY_PREFIX}${groupId}`;
  const existing = localStorage.getItem(keyName);
  if (!existing) return null;
  return naclUtil.decodeBase64(existing);
}

function storeGroupSenderKeyLocal(groupId: string, key: Uint8Array): void {
  const keyName = `${GROUP_SENDER_KEY_PREFIX}${groupId}`;
  localStorage.setItem(keyName, naclUtil.encodeBase64(key));
}

class LibsignalLocalStorageStore {
  // Direction enum required by libsignal-protocol's SessionBuilder/SessionCipher
  Direction = {
    SENDING: 1,
    RECEIVING: 2
  };
  private readonly prefix: string;

  constructor(private readonly userId: string) {
    this.prefix = `${STORAGE_PREFIX}_${userId}_`;
  }

  private k(name: string) {
    return `${this.prefix}${name}`;
  }

  async getIdentityKeyPair(): Promise<any | undefined> {
    const pubB64 = localStorage.getItem(this.k('identity_pub'));
    const privB64 = localStorage.getItem(this.k('identity_priv'));
    if (!pubB64 || !privB64) return undefined;
    return { pubKey: b64ToAb(pubB64), privKey: b64ToAb(privB64) };
  }

  async getLocalRegistrationId(): Promise<number | undefined> {
    const reg = localStorage.getItem(this.k('registration_id'));
    if (!reg) return undefined;
    const n = Number(reg);
    return Number.isFinite(n) ? n : undefined;
  }

  async isTrustedIdentity(identifier: string, identityKey: ArrayBuffer): Promise<boolean> {
    // TOFU: trust on first use per identifier
    const existing = localStorage.getItem(this.k(`trusted_identity_${identifier}`));
    if (!existing) return true;
    const existingAb = b64ToAb(existing);
    const a = new Uint8Array(existingAb);
    const b = new Uint8Array(identityKey);
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  async saveIdentity(encodedAddress: string, publicKey: ArrayBuffer): Promise<boolean> {
    const key = this.k(`trusted_identity_${encodedAddress}`);
    const existing = localStorage.getItem(key);
    const newB64 = abToB64(publicKey);
    if (existing && existing === newB64) return false;
    localStorage.setItem(key, newB64);
    return true;
  }

  async loadPreKey(keyId: number | string): Promise<any | undefined> {
    const id = String(keyId);
    const pub = localStorage.getItem(this.k(`prekey_${id}_pub`));
    const priv = localStorage.getItem(this.k(`prekey_${id}_priv`));
    if (!pub || !priv) return undefined;
    return { pubKey: b64ToAb(pub), privKey: b64ToAb(priv) };
  }

  async storePreKey(keyId: number | string, keyPair: any): Promise<void> {
    const id = String(keyId);
    localStorage.setItem(this.k(`prekey_${id}_pub`), abToB64(keyPair.pubKey));
    localStorage.setItem(this.k(`prekey_${id}_priv`), abToB64(keyPair.privKey));
  }

  async removePreKey(keyId: number | string): Promise<void> {
    const id = String(keyId);
    localStorage.removeItem(this.k(`prekey_${id}_pub`));
    localStorage.removeItem(this.k(`prekey_${id}_priv`));
  }

  async storeSession(encodedAddress: string, record: string): Promise<void> {
    localStorage.setItem(this.k(`session_${encodedAddress}`), record);
  }

  async loadSession(encodedAddress: string): Promise<string | undefined> {
    return localStorage.getItem(this.k(`session_${encodedAddress}`)) ?? undefined;
  }

  async loadSignedPreKey(keyId: number | string): Promise<any | undefined> {
    const id = String(keyId);
    const pub = localStorage.getItem(this.k(`signedprekey_${id}_pub`));
    const priv = localStorage.getItem(this.k(`signedprekey_${id}_priv`));
    if (!pub || !priv) return undefined;
    return { pubKey: b64ToAb(pub), privKey: b64ToAb(priv) };
  }

  async storeSignedPreKey(keyId: number | string, keyPair: any): Promise<void> {
    const id = String(keyId);
    localStorage.setItem(this.k(`signedprekey_${id}_pub`), abToB64(keyPair.pubKey));
    localStorage.setItem(this.k(`signedprekey_${id}_priv`), abToB64(keyPair.privKey));
  }

  async removeSignedPreKey(keyId: number | string): Promise<void> {
    const id = String(keyId);
    localStorage.removeItem(this.k(`signedprekey_${id}_pub`));
    localStorage.removeItem(this.k(`signedprekey_${id}_priv`));
  }

  setIdentityKeyPair(identity: any) {
    localStorage.setItem(this.k('identity_pub'), abToB64(identity.pubKey));
    localStorage.setItem(this.k('identity_priv'), abToB64(identity.privKey));
  }
  setRegistrationId(regId: number) {
    localStorage.setItem(this.k('registration_id'), String(regId));
  }
}

async function uploadPreKeyBundleV2(userId: string, store: LibsignalLocalStorageStore) {
  const ls = libsignal();
  const identity = await store.getIdentityKeyPair();
  const regId = await store.getLocalRegistrationId();
  if (!identity || regId === undefined) throw new Error('Missing local identity/registrationId');

  const signedIdHigh = Math.floor(Math.random() * 131072); // 17 bits
  const signedPreKeyId = packSignedPreKeyId(signedIdHigh, regId);
  const signedPreKey = await ls.KeyHelper.generateSignedPreKey(identity, signedPreKeyId);
  await store.storeSignedPreKey(signedPreKeyId, signedPreKey.keyPair);

  const numPreKeys = 50;
  const preKeys: { key_id: number; public_key: string; user_id: string }[] = [];
  const baseId = Date.now() % 2147480000;
  for (let i = 0; i < numPreKeys; i++) {
    const keyId = (baseId + i) % 2147483647;
    const pk = await ls.KeyHelper.generatePreKey(keyId);
    await store.storePreKey(pk.keyId, pk.keyPair);
    preKeys.push({ user_id: userId, key_id: pk.keyId, public_key: abToB64(pk.keyPair.pubKey) });
  }

  const { error: updateError } = await supabase
    .from('users')
    .update({
      public_key: abToB64(identity.pubKey),
      signed_prekey_id: signedPreKeyId,
      signed_prekey: abToB64(signedPreKey.keyPair.pubKey),
      signed_prekey_signature: abToB64(signedPreKey.signature),
      prekey_bundle_updated_at: new Date().toISOString(),
    })
    .eq('id', userId);

  if (updateError) throw updateError;

  await supabase.from('prekeys').delete().eq('user_id', userId).is('used_at', null);
  const { error: insertError } = await supabase.from('prekeys').insert(preKeys);
  if (insertError) throw insertError;
}

export async function ensureLibsignalV2Initialized(userId: string): Promise<void> {
  const ls = libsignal();
  const store = new LibsignalLocalStorageStore(userId);
  
  // MIGRATION: Force clear ALL keys on first run to ensure Curve25519 format
  const migrationKey = 'signal_v2_migration_curve25519_done';
  const migrationDone = localStorage.getItem(migrationKey);
  
  if (!migrationDone) {
    debug.info('Running one-time migration to Curve25519 keys...');
    
    // Clear ALL localStorage keys for ALL users
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    
    // Clear this user's keys from database
    try {
      await supabase.from('users').update({
        public_key: null,
        signed_prekey_id: null,
        signed_prekey: null,
        signed_prekey_signature: null,
        prekey_bundle_updated_at: null,
      }).eq('id', userId);
      
      await supabase.from('prekeys').delete().eq('user_id', userId);
    } catch (e) {
      debug.warn('Failed to clear database keys during migration', { error: String(e) });
    }
    
    localStorage.setItem(migrationKey, 'true');
  }
  
  // Check for migration: if we have old keys (wrong length), force regeneration
  let identity = await store.getIdentityKeyPair();
  let regId = await store.getLocalRegistrationId();
  
  // Check if identity key is the wrong format (Ed25519 = 32 bytes, Curve25519 = 33 bytes with prefix)
  const needsMigration = identity && new Uint8Array(identity.pubKey).length !== 33;
  
  if (!identity || regId === undefined || needsMigration) {
    if (needsMigration) {
      debug.info('Detected wrong key format, regenerating...');
    }
    
    const newIdentity = await ls.KeyHelper.generateIdentityKeyPair();
    const newRegId = Number(ls.KeyHelper.generateRegistrationId());
    if (!Number.isFinite(newRegId)) {
      throw new Error('Failed to generate registrationId');
    }
    regId = newRegId;
    store.setIdentityKeyPair(newIdentity);
    store.setRegistrationId(newRegId);
    identity = newIdentity;
  }

  try {
    const { data } = await supabase
      .from('users')
      .select('prekey_bundle_updated_at, signed_prekey_id, signed_prekey, signed_prekey_signature, public_key')
      .eq('id', userId)
      .single();

    const shouldUpload =
      !data?.public_key ||
      !data?.signed_prekey ||
      !data?.signed_prekey_signature ||
      data?.signed_prekey_id === null ||
      !data?.prekey_bundle_updated_at ||
      Date.now() - new Date(data.prekey_bundle_updated_at).getTime() > 30 * 24 * 60 * 60 * 1000;

    if (shouldUpload) {
      await uploadPreKeyBundleV2(userId, store);
    }
  } catch (e) {
    debug.warn('Libsignal v2 prekey bundle check/upload failed (will retry later)', { error: String(e) });
  }
}

async function fetchLibsignalDeviceBundle(userId: string) {
  const { data: user, error } = await supabase
    .from('users')
    .select('public_key, signed_prekey_id, signed_prekey, signed_prekey_signature')
    .eq('id', userId)
    .single();

  if (error || !user?.public_key || !user?.signed_prekey || !user?.signed_prekey_signature) {
    throw new Error(`Missing libsignal bundle for user ${userId}`);
  }

  const { data: prekeyRows } = await supabase
    .from('prekeys')
    .select('key_id, public_key')
    .eq('user_id', userId)
    .is('used_at', null)
    .limit(1);
  
  const prekeyRow = prekeyRows?.[0];

  if (prekeyRow?.key_id) {
    try {
      await supabase
        .from('prekeys')
        .update({ used_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('key_id', prekeyRow.key_id);
    } catch {
      // ignore
    }
  }

  const signedPreKeyId: number = user.signed_prekey_id;
  const registrationId = unpackRegistrationIdFromSignedPreKeyId(signedPreKeyId);

  return {
    registrationId,
    identityKey: b64ToAb(user.public_key),
    signedPreKey: {
      keyId: signedPreKeyId,
      publicKey: b64ToAb(user.signed_prekey),
      signature: b64ToAb(user.signed_prekey_signature),
    },
    preKey: prekeyRow
      ? {
          keyId: prekeyRow.key_id,
          publicKey: b64ToAb(prekeyRow.public_key),
        }
      : undefined,
  };
}

async function ensureSessionAsInitiator(localUserId: string, remoteUserId: string): Promise<LibsignalLocalStorageStore> {
  const ls = libsignal();
  const store = new LibsignalLocalStorageStore(localUserId);
  await ensureLibsignalV2Initialized(localUserId);

  const address = new ls.SignalProtocolAddress(remoteUserId, DEVICE_ID);
  const cipher = new ls.SessionCipher(store, address);
  const has = await cipher.hasOpenSession().catch(() => false);
  if (has) return store;

  const device = await fetchLibsignalDeviceBundle(remoteUserId);
  const builder = new ls.SessionBuilder(store, address);
  await builder.processPreKey(device);
  return store;
}

export async function encryptDirectV2(senderId: string, recipientId: string, plaintext: string): Promise<SignalV2Envelope> {
  const ls = libsignal();
  const store = await ensureSessionAsInitiator(senderId, recipientId);
  const address = new ls.SignalProtocolAddress(recipientId, DEVICE_ID);
  const cipher = new ls.SessionCipher(store, address);

  const plaintextBytes = naclUtil.decodeUTF8(plaintext);
  const encrypted = await cipher.encrypt(uint8ToArrayBuffer(plaintextBytes));
  const bodyB64 = encrypted.body ? binaryStringToBase64(encrypted.body) : '';

  const senderCopyKey = ensureSenderCopyKey(senderId);
  const nonce = nacl.randomBytes(24);
  const ct = nacl.secretbox(plaintextBytes, nonce, senderCopyKey);
  const senderCopy = { nonceB64: naclUtil.encodeBase64(nonce), ciphertextB64: naclUtil.encodeBase64(ct) };

  return {
    v: 2,
    protocol: 'libsignal',
    kind: 'direct',
    from: senderId,
    to: recipientId,
    recipient: { type: encrypted.type, bodyB64 },
    senderCopy,
  };
}

export async function decryptDirectV2(userId: string, senderId: string, env: SignalV2Envelope): Promise<string> {
  if (env.kind !== 'direct') throw new Error('Not a direct envelope');

  if (userId === env.from && env.senderCopy) {
    const key = ensureSenderCopyKey(userId);
    const nonce = naclUtil.decodeBase64(env.senderCopy.nonceB64);
    const ct = naclUtil.decodeBase64(env.senderCopy.ciphertextB64);
    const pt = nacl.secretbox.open(ct, nonce, key);
    if (!pt) throw new Error('Failed to decrypt senderCopy');
    return naclUtil.encodeUTF8(pt);
  }

  const ls = libsignal();
  await ensureLibsignalV2Initialized(userId);
  const store = new LibsignalLocalStorageStore(userId);
  const address = new ls.SignalProtocolAddress(senderId, DEVICE_ID);
  const cipher = new ls.SessionCipher(store, address);

  const binary = base64ToBinaryString(env.recipient.bodyB64);
  const plaintextAb =
    env.recipient.type === 3
      ? await cipher.decryptPreKeyWhisperMessage(binary, 'binary')
      : await cipher.decryptWhisperMessage(binary, 'binary');

  return naclUtil.encodeUTF8(arrayBufferToUint8(plaintextAb));
}

export async function encryptGroupV2(senderId: string, groupId: string, memberIds: string[], plaintext: string): Promise<SignalV2Envelope> {
  await ensureLibsignalV2Initialized(senderId);

  let senderKey = getGroupSenderKeyLocal(groupId);
  if (!senderKey) {
    senderKey = nacl.randomBytes(32);
    storeGroupSenderKeyLocal(groupId, senderKey);
  }

  const nonce = nacl.randomBytes(24);
  const ptBytes = naclUtil.decodeUTF8(plaintext);
  const ct = nacl.secretbox(ptBytes, nonce, senderKey);

  const encryptedSenderKeys: Record<string, LibsignalCiphertext> = {};
  for (const memberId of memberIds) {
    if (memberId === senderId) continue;
    const store = await ensureSessionAsInitiator(senderId, memberId);
    const ls = libsignal();
    const address = new ls.SignalProtocolAddress(memberId, DEVICE_ID);
    const cipher = new ls.SessionCipher(store, address);
    const enc = await cipher.encrypt(uint8ToArrayBuffer(senderKey));
    encryptedSenderKeys[memberId] = { type: enc.type, bodyB64: enc.body ? binaryStringToBase64(enc.body) : '' };
  }

  return {
    v: 2,
    protocol: 'libsignal',
    kind: 'group',
    groupId,
    from: senderId,
    payload: { nonceB64: naclUtil.encodeBase64(nonce), ciphertextB64: naclUtil.encodeBase64(ct) },
    encryptedSenderKeys,
  };
}

export async function decryptGroupV2(userId: string, groupId: string, senderId: string, env: SignalV2Envelope): Promise<string> {
  if (env.kind !== 'group' || env.groupId !== groupId) throw new Error('Not a group envelope');

  if (userId === senderId) {
    const senderKey = getGroupSenderKeyLocal(groupId);
    if (!senderKey) throw new Error('Missing local group sender key for sender');
    const nonce = naclUtil.decodeBase64(env.payload.nonceB64);
    const ct = naclUtil.decodeBase64(env.payload.ciphertextB64);
    const pt = nacl.secretbox.open(ct, nonce, senderKey);
    if (!pt) throw new Error('Failed to decrypt group message with local sender key');
    return naclUtil.encodeUTF8(pt);
  }

  const encryptedKeyMsg = env.encryptedSenderKeys[userId];
  if (!encryptedKeyMsg) throw new Error('Missing encrypted sender key for recipient');

  const ls = libsignal();
  await ensureLibsignalV2Initialized(userId);
  const store = new LibsignalLocalStorageStore(userId);
  const address = new ls.SignalProtocolAddress(senderId, DEVICE_ID);
  const cipher = new ls.SessionCipher(store, address);

  const binary = base64ToBinaryString(encryptedKeyMsg.bodyB64);
  const senderKeyAb =
    encryptedKeyMsg.type === 3
      ? await cipher.decryptPreKeyWhisperMessage(binary, 'binary')
      : await cipher.decryptWhisperMessage(binary, 'binary');

  const senderKey = arrayBufferToUint8(senderKeyAb);
  const nonce = naclUtil.decodeBase64(env.payload.nonceB64);
  const ct = naclUtil.decodeBase64(env.payload.ciphertextB64);
  const pt = nacl.secretbox.open(ct, nonce, senderKey);
  if (!pt) throw new Error('Failed to decrypt group message payload');
  return naclUtil.encodeUTF8(pt);
}


